import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import GenericRichEditor from 'components/GenericRichEditor/GenericRichEditor';
import IconButton from 'components/IconButton';
import Markdown from 'components/Markdown';
import ModalDialog from 'components/ModalDialog';
import Skeleton from 'components/Skeleton';
import Tooltip from 'components/Tooltip';
import { EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import { useAtomValue, useSetAtom } from 'jotai';
import { extractApiErrorMessage } from 'lib/helper';
import {
  showFailureToast,
  showSuccessToast,
  showWarningToast,
} from 'lib/ToastHelper';
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

import {
  createBPAFindingNote,
  deleteBPAFindingNote,
  fetchBPAFindingNotes,
  markBPAFindingNotesAsRead,
  updateBPAFindingNote,
} from '../../services/bpaFindingsService';
import { findingsDataRefreshAtom } from '../../state';
import { BPANote, BPANotesResponse } from '../../types/bpaFindings.types';
import './BPANotesModal.css';

interface BPANotesModalProps {
  toggle: () => void;
  findingId: number;
  haveUnreadNotes: boolean;
  title: string;
}

const NOTES_PAGE_SIZE = 10;

interface FetchNotesOptions {
  page?: number;
  append?: boolean;
  markUnreadAsRead?: boolean;
  isCancelled?: () => boolean;
}

const getNextNotesPage = (
  response: BPANotesResponse,
  currentPage: number,
): number | null => (response.next ? currentPage + 1 : null);

const BPANotesModal: React.FC<BPANotesModalProps> = ({
  toggle,
  findingId,
  haveUnreadNotes,
  title,
}) => {
  const companyId = useAtomValue(companyIdAtom);
  const refreshFindingsGridData = useSetAtom(findingsDataRefreshAtom);
  const isMountedRef = useRef(true);

  const [notes, setNotes] = useState<BPANote[]>([]);
  const [notesCount, setNotesCount] = useState(0);
  const [nextNotesPage, setNextNotesPage] = useState<number | null>(null);
  const [highlightedNoteIds, setHighlightedNoteIds] = useState<number[]>([]);
  const [shouldHighlight, setShouldHighlight] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMoreNotes, setLoadingMoreNotes] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [savingEditedNote, setSavingEditedNote] = useState(false);
  const [deletingNote, setDeletingNote] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [editorEditedState, setEditorEditedState] = useState(
    EditorState.createEmpty(),
  );
  const [editedNoteId, setEditedNoteId] = useState<number | null>(null);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<number | null>(null);

  const refreshGrid = useCallback(
    () => refreshFindingsGridData(new Date().toISOString()),
    [refreshFindingsGridData],
  );

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const isComponentUnmounted = useCallback(() => !isMountedRef.current, []);

  const markAllAsRead = useCallback(
    async (isCancelled = isComponentUnmounted) => {
      if (!companyId || companyId === -1) return;

      try {
        await markBPAFindingNotesAsRead({
          company_id: companyId,
          finding_ids: [findingId],
        });
        if (!isCancelled()) {
          refreshGrid();
        }
      } catch {
        if (!isCancelled()) {
          showFailureToast('Failed to mark notes as read.');
        }
      }
    },
    [companyId, findingId, isComponentUnmounted, refreshGrid],
  );

  const fetchNotes = useCallback(
    async ({
      page = 1,
      append = false,
      markUnreadAsRead = false,
      isCancelled = isComponentUnmounted,
    }: FetchNotesOptions = {}) => {
      if (!companyId || companyId === -1) return;

      if (append) {
        setLoadingMoreNotes(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await fetchBPAFindingNotes({
          company_id: companyId,
          finding_id: findingId,
          ordering: '-created_at',
          page,
          page_size: NOTES_PAGE_SIZE,
        });

        if (isCancelled()) return;

        const fetchedNotes = response.results ?? [];
        setNotes((prev) => {
          if (!append) return fetchedNotes;

          const loadedNoteIds = new Set(prev.map((note) => note.id));
          const uniqueFetchedNotes = fetchedNotes.filter(
            (note) => !loadedNoteIds.has(note.id),
          );
          return [...prev, ...uniqueFetchedNotes];
        });
        setNotesCount(response.count ?? fetchedNotes.length);
        setNextNotesPage(getNextNotesPage(response, page));

        if (markUnreadAsRead) {
          await markAllAsRead(isCancelled);
        }
      } catch (err) {
        if (!isCancelled()) {
          showFailureToast(
            extractApiErrorMessage(err, 'Failed to load notes.'),
          );
        }
      } finally {
        if (!isCancelled()) {
          if (append) {
            setLoadingMoreNotes(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    [companyId, findingId, isComponentUnmounted, markAllAsRead],
  );

  useEffect(() => {
    if (!findingId) return;

    let cancelled = false;
    const isCancelled = () => cancelled || isComponentUnmounted();

    setNotes([]);
    setNotesCount(0);
    setNextNotesPage(null);
    setHighlightedNoteIds([]);
    setEditorState(EditorState.createEmpty());
    setShouldHighlight(true);
    void fetchNotes({ markUnreadAsRead: haveUnreadNotes, isCancelled });

    return () => {
      cancelled = true;
    };
  }, [fetchNotes, findingId, haveUnreadNotes, isComponentUnmounted]);

  useEffect(() => {
    if (notes.length === 0 || !shouldHighlight) return;

    const unreadIds = notes
      .filter((note) => note.is_read === false)
      .map((note) => note.id);

    if (unreadIds.length === 0) return;

    setHighlightedNoteIds(unreadIds);
    const timeout = window.setTimeout(() => {
      setHighlightedNoteIds([]);
      setShouldHighlight(false);
    }, 6000);

    return () => window.clearTimeout(timeout);
  }, [notes, shouldHighlight]);

  const addNote = async () => {
    if (!companyId || companyId === -1) {
      showFailureToast('Please select a company before adding notes.');
      return;
    }

    const markdownContent = draftToMarkdown(
      convertToRaw(editorState.getCurrentContent()),
    );

    if (!markdownContent.trim()) {
      showWarningToast('Note content cannot be empty.');
      return;
    }

    setAddingNote(true);

    try {
      const response = await createBPAFindingNote({
        company_id: companyId,
        finding_id: findingId,
        content: markdownContent,
      });

      if (response.status === 200 || response.status === 201) {
        setNotes((prev) => [response.data as BPANote, ...prev]);
        setNotesCount((prev) => prev + 1);
        setEditorState(EditorState.createEmpty());
        showSuccessToast('Note added successfully.');
        refreshGrid();
      } else {
        throw new Error('Failed to add note.');
      }
    } catch (err) {
      showFailureToast(extractApiErrorMessage(err, 'Failed to add note.'));
    } finally {
      setAddingNote(false);
    }
  };

  const startEditNote = (note: BPANote) => {
    const contentState = markdownToDraft(note.content);
    setEditorEditedState(
      EditorState.createWithContent(convertFromRaw(contentState)),
    );
    setEditedNoteId(note.id);
  };

  const cancelEdit = () => {
    setEditedNoteId(null);
    setEditorEditedState(EditorState.createEmpty());
  };

  const saveEditedNote = async () => {
    if (!companyId || companyId === -1 || !editedNoteId) return;

    const markdownContent = draftToMarkdown(
      convertToRaw(editorEditedState.getCurrentContent()),
    );

    if (!markdownContent.trim()) {
      showWarningToast('Note content cannot be empty.');
      return;
    }

    setSavingEditedNote(true);

    try {
      const response = await updateBPAFindingNote(editedNoteId, {
        company_id: companyId,
        content: markdownContent,
      });

      if (response.status === 200) {
        setNotes((prev) =>
          prev.map((note) =>
            note.id === editedNoteId
              ? { ...note, ...(response.data as BPANote) }
              : note,
          ),
        );
        setEditedNoteId(null);
        setEditorEditedState(EditorState.createEmpty());
        showSuccessToast('Note updated successfully.');
      } else {
        throw new Error('Failed to update note.');
      }
    } catch (err) {
      showFailureToast(extractApiErrorMessage(err, 'Failed to update note.'));
    } finally {
      setSavingEditedNote(false);
    }
  };

  const confirmDeleteNote = (noteId: number) => {
    setNoteToDelete(noteId);
    setShowConfirmDeleteModal(true);
  };

  const cancelDelete = () => {
    setNoteToDelete(null);
    setShowConfirmDeleteModal(false);
  };

  const deleteNote = async () => {
    if (!companyId || companyId === -1 || !noteToDelete) return;

    setDeletingNote(true);

    try {
      await deleteBPAFindingNote(noteToDelete, companyId);
      setNotes((prev) => prev.filter((note) => note.id !== noteToDelete));
      setNotesCount((prev) => Math.max(prev - 1, 0));
      setShowConfirmDeleteModal(false);
      setNoteToDelete(null);
      showSuccessToast('Note deleted successfully.');
      refreshGrid();
    } catch (err) {
      showFailureToast(extractApiErrorMessage(err, 'Failed to delete note.'));
    } finally {
      setDeletingNote(false);
    }
  };

  const isCurrentUserAuthor = (authorEmail: string) =>
    authorEmail === localStorage.getItem('loggedInUser');

  const isNoteModified = (note: BPANote) => {
    const createdDate = moment(note.created_at).format('YYYY-MM-DD HH:mm:ss');
    const updatedDate = moment(note.updated_at).format('YYYY-MM-DD HH:mm:ss');
    return createdDate !== updatedDate;
  };

  const renderDeleteConfirmModal = () => (
    <Modal isOpen={showConfirmDeleteModal} size="sm" zIndex={9999}>
      <ModalHeader>
        <h4>Delete Confirmation</h4>
      </ModalHeader>
      <ModalBody>
        <p>Are you sure you want to delete this Note?</p>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={cancelDelete}>
          Cancel
        </Button>
        <Button
          color="danger"
          disabled={deletingNote}
          onClick={() => void deleteNote()}
          style={{ minWidth: '80px' }}
        >
          {deletingNote ? (
            <div className="d-flex align-items-center justify-content-center">
              <CircleSpinner size={20} />
              <span className="ml-1">Deleting...</span>
            </div>
          ) : (
            'Delete'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );

  const renderSkeletons = () =>
    Array(2)
      .fill(0)
      .map((_, index) => (
        <Card key={`skeleton-${index}`} className="mb-3 shadow-sm">
          <CardHeader className="d-flex justify-content-between align-items-center bg-light mb-2">
            <div className="d-flex align-items-center">
              <Skeleton width="32px" height="32px" rounded="full" />
              <div className="ml-2">
                <Skeleton width="120px" height="18px" rounded="md" />
              </div>
            </div>
            <Skeleton width="60px" height="24px" rounded="md" />
          </CardHeader>
          <CardBody>
            <Skeleton width="100%" height="20px" rounded="md" />
            <Skeleton
              width="90%"
              height="20px"
              rounded="md"
              style={{ marginTop: '8px' }}
            />
            <Skeleton
              width="80%"
              height="20px"
              rounded="md"
              style={{ marginTop: '8px' }}
            />
          </CardBody>
        </Card>
      ));

  const renderNotesContent = () => {
    if (loading) {
      return renderSkeletons();
    }

    if (notes.length === 0) {
      return (
        <div className="text-center py-5">
          <div className="mb-3">
            <i
              className="tim-icons icon-chat-33 text-muted"
              style={{ fontSize: '3rem' }}
            />
          </div>
          <h5 className="text-muted">No notes yet</h5>
          <p className="text-muted">
            Be the first to add a note to this finding.
          </p>
        </div>
      );
    }

    const hasMoreNotes = nextNotesPage !== null;

    return (
      <>
        {notes.map((note) => {
          const canEdit = isCurrentUserAuthor(note.author_email);
          const authorInitial = (note.author_name || note.author_email || '?')
            .charAt(0)
            .toUpperCase();

          return (
            <Card
              key={note.id}
              className={`mb-3 shadow-sm${
                highlightedNoteIds.includes(note.id)
                  ? ' note-highlight-fade'
                  : ''
              }`}
            >
              <CardHeader className="d-flex justify-content-between align-items-center bg-light">
                <div className="d-flex align-items-center">
                  <div
                    className="avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mr-2"
                    style={{ width: '32px', height: '32px' }}
                  >
                    {authorInitial}
                  </div>
                  <div className="ml-2">
                    <div className="d-flex align-items-center">
                      <div className="font-weight-bold mr-2">
                        {note.author_name || note.author_email}
                      </div>
                    </div>
                    <div>
                      <small className="text-muted">
                        {moment(note.created_at).format('MMM D, YYYY h:mm A')}
                      </small>
                      {isNoteModified(note) && (
                        <Tooltip
                          tooltip={
                            <div>
                              <div className="font-weight-bold mb-1">
                                Note was edited
                              </div>
                              <div>
                                Original:{' '}
                                {moment(note.created_at).format(
                                  'MMM D, YYYY h:mm A',
                                )}
                              </div>
                              <div>
                                Modified:{' '}
                                {moment(note.updated_at).format(
                                  'MMM D, YYYY h:mm A',
                                )}
                              </div>
                            </div>
                          }
                          place="bottom"
                        >
                          <span
                            className="badge badge-pill ml-2"
                            style={{ fontSize: '10px', padding: '3px 6px' }}
                          >
                            <i className="fas fa-pen mr-1" />
                            edited
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
                {editedNoteId === note.id ? (
                  <div>
                    <IconButton
                      iconClass="fas fa-check"
                      tooltip="Update Note"
                      onClick={() => void saveEditedNote()}
                      id={`save-bpa-note-${note.id}`}
                      disabled={savingEditedNote}
                    />
                    <IconButton
                      iconClass="fas fa-times"
                      tooltip="Discard Changes"
                      onClick={cancelEdit}
                      id={`cancel-bpa-note-${note.id}`}
                      disabled={savingEditedNote}
                    />
                  </div>
                ) : (
                  <div>
                    <IconButton
                      id={`edit-bpa-note-${note.id}`}
                      iconClass="fas fa-edit"
                      tooltip={
                        canEdit ? 'Edit Note' : 'You cannot edit this note'
                      }
                      onClick={() => startEditNote(note)}
                      disabled={editedNoteId !== null || !canEdit}
                    />
                    <IconButton
                      id={`delete-bpa-note-${note.id}`}
                      iconClass="fas fa-trash"
                      tooltip={
                        canEdit ? 'Delete Note' : 'You cannot delete this note'
                      }
                      onClick={() => confirmDeleteNote(note.id)}
                      disabled={editedNoteId !== null || !canEdit}
                    />
                  </div>
                )}
              </CardHeader>
              <CardBody>
                {editedNoteId === note.id ? (
                  <div className="note-editor-container">
                    <GenericRichEditor
                      editorState={editorEditedState}
                      setEditorState={setEditorEditedState}
                      placeholder="Edit your note..."
                    />
                  </div>
                ) : (
                  <div style={{ marginTop: '15px' }}>
                    <Markdown>{note.content}</Markdown>
                  </div>
                )}
              </CardBody>
            </Card>
          );
        })}
        {hasMoreNotes && (
          <div className="d-flex justify-content-center my-2">
            <Button
              color="secondary"
              size="sm"
              onClick={() =>
                void fetchNotes({
                  page: nextNotesPage ?? 1,
                  append: true,
                })
              }
              disabled={loadingMoreNotes}
            >
              {loadingMoreNotes ? (
                <div className="d-flex align-items-center justify-content-center">
                  <CircleSpinner size={18} />
                  <span className="ml-1">Loading...</span>
                </div>
              ) : (
                `Load more notes (${notes.length} of ${notesCount})`
              )}
            </Button>
          </div>
        )}
      </>
    );
  };

  const notesContent = (
    <CustomModalWrapper id="bpa-notes-scroll">
      <h5>Existing Notes</h5>
      <CustomModalWrapper id="bpa-existing-notes-scroll" height="40vh">
        <div style={{ minHeight: '40vh' }}>{renderNotesContent()}</div>
      </CustomModalWrapper>

      <Card className="editorContainer">
        <br />
        <h5>Add a new note</h5>
        <GenericRichEditor
          editorState={editorState}
          setEditorState={setEditorState}
          placeholder="Type your note here..."
        />
      </Card>
    </CustomModalWrapper>
  );

  const customFooter = (
    <Row className="w-100">
      <Col className="text-left">
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </Col>
      <Col className="text-right">
        <Button
          color="primary"
          onClick={() => void addNote()}
          disabled={addingNote || editedNoteId !== null}
          style={{ minWidth: '100px' }}
        >
          {addingNote ? (
            <div className="d-flex align-items-center justify-content-center">
              <CircleSpinner size={20} />
              <span className="ml-1">Adding...</span>
            </div>
          ) : (
            'Add Note'
          )}
        </Button>
      </Col>
    </Row>
  );

  return (
    <>
      <ModalDialog
        title={`Notes for "${title}"`}
        onClose={toggle}
        childComponent={notesContent}
        customFooter={customFooter}
        size="lg"
      />
      {showConfirmDeleteModal && renderDeleteConfirmModal()}
    </>
  );
};

export default BPANotesModal;
